import React, { useState, useEffect } from 'react';
import { getProjects, createProject, deleteProject } from '../services/api';
import Button from '../components/ui/Button';
import { Trash2, Plus, Loader } from 'lucide-react';

const Admin = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        // Shared
        year: '',
        client: '',
        images: [],

        // Serbian
        title_sr: '',
        category_sr: '',
        description_sr: '',
        location_sr: '',
        status_sr: 'Završeno',

        // English
        title_en: '',
        category_en: '',
        description_en: '',
        location_en: '',
        status_en: 'Completed',
    });

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            const data = await getProjects();
            setProjects(data);
        } catch (error) {
            console.error("Error fetching projects:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        // Store multiple files
        setFormData(prev => ({ ...prev, images: e.target.files }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const data = new FormData();

        // Append all text fields
        Object.keys(formData).forEach(key => {
            if (key !== 'images') {
                data.append(key, formData[key]);
            }
        });

        // Append images
        if (formData.images && formData.images.length > 0) {
            for (let i = 0; i < formData.images.length; i++) {
                data.append('images', formData.images[i]);
            }
        }

        try {
            await createProject(data);
            await fetchProjects();
            // Reset form
            setFormData({
                year: '',
                client: '',
                images: [],
                title_sr: '', category_sr: '', description_sr: '', location_sr: '', status_sr: 'Završeno',
                title_en: '', category_en: '', description_en: '', location_en: '', status_en: 'Completed',
            });
            alert('Project created successfully!');
        } catch (error) {
            console.error("Error creating project:", error);
            alert('Failed to create project.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this project?')) {
            try {
                await deleteProject(id);
                await fetchProjects();
            } catch (error) {
                console.error("Error deleting project:", error);
                alert('Failed to delete project.');
            }
        }
    };

    // Helper to safely get title (handles old string format vs new object format)
    const getDisplayTitle = (project) => {
        if (typeof project.title === 'object' && project.title !== null) {
            return project.title.sr || project.title.en || 'No Title';
        }
        return project.title; // Backward compatibility
    };

    return (
        <div className="min-h-screen bg-slate-50 p-8">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-3xl font-bold text-slate-900 mb-8">Admin Dashboard</h1>

                <div className="grid lg:grid-cols-2 gap-8">
                    {/* Form Section */}
                    <div className="bg-white p-6 rounded-xl shadow-md h-fit">
                        <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                            <Plus className="w-5 h-5 text-gold-500" /> Add New Project
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-6">

                            {/* Shared Info */}
                            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                                <h3 className="font-semibold text-slate-700 mb-4 border-b pb-2">Shared Info</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Year</label>
                                        <input name="year" value={formData.year} onChange={handleInputChange} className="w-full p-2 border rounded" placeholder="2024" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Client</label>
                                        <input name="client" value={formData.client} onChange={handleInputChange} className="w-full p-2 border rounded" placeholder="Investitor..." />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Images (Select Multiple)</label>
                                        <input required type="file" multiple onChange={handleFileChange} className="w-full p-2 border rounded" accept="image/*" />
                                    </div>
                                </div>
                            </div>

                            {/* Serbian Section */}
                            <div className="bg-blue-50 p-4 rounded-lg border border-slate-200">
                                <h3 className="font-semibold text-blue-800 mb-4 border-b border-blue-200 pb-2 flex items-center gap-2">
                                    <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded">SR</span> Srpski
                                </h3>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Naslov Projekta</label>
                                        <input required name="title_sr" value={formData.title_sr} onChange={handleInputChange} className="w-full p-2 border rounded" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Kategorija</label>
                                            <input required name="category_sr" value={formData.category_sr} onChange={handleInputChange} className="w-full p-2 border rounded" placeholder="Stambeni objekat..." />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Lokacija</label>
                                            <input name="location_sr" value={formData.location_sr} onChange={handleInputChange} className="w-full p-2 border rounded" placeholder="Novi Pazar..." />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                                        <input name="status_sr" value={formData.status_sr} onChange={handleInputChange} className="w-full p-2 border rounded" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Opis</label>
                                        <textarea required name="description_sr" value={formData.description_sr} onChange={handleInputChange} rows="3" className="w-full p-2 border rounded" />
                                    </div>
                                </div>
                            </div>

                            {/* English Section */}
                            <div className="bg-green-50 p-4 rounded-lg border border-slate-200">
                                <h3 className="font-semibold text-green-800 mb-4 border-b border-green-200 pb-2 flex items-center gap-2">
                                    <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded">EN</span> English
                                </h3>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Project Title</label>
                                        <input required name="title_en" value={formData.title_en} onChange={handleInputChange} className="w-full p-2 border rounded" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                                            <input required name="category_en" value={formData.category_en} onChange={handleInputChange} className="w-full p-2 border rounded" placeholder="Residential..." />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                                            <input name="location_en" value={formData.location_en} onChange={handleInputChange} className="w-full p-2 border rounded" placeholder="Novi Pazar, Serbia" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                                        <input name="status_en" value={formData.status_en} onChange={handleInputChange} className="w-full p-2 border rounded" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                                        <textarea required name="description_en" value={formData.description_en} onChange={handleInputChange} rows="3" className="w-full p-2 border rounded" />
                                    </div>
                                </div>
                            </div>

                            <Button type="submit" variant="primary" className="w-full" disabled={loading}>
                                {loading ? <Loader className="animate-spin" /> : 'Create Project'}
                            </Button>
                        </form>
                    </div>

                    {/* List Section */}
                    <div className="space-y-4">
                        <h2 className="text-xl font-bold mb-6">Existing Projects</h2>
                        {projects.map(project => (
                            <div key={project.id} className="bg-white p-4 rounded-lg shadow-sm flex items-center gap-4">
                                <img
                                    src={project.image}
                                    alt="Project thumbnail"
                                    className="w-24 h-24 object-cover rounded bg-slate-100"
                                />
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-bold text-slate-900 truncate">{getDisplayTitle(project)}</h3>
                                    <p className="text-sm text-slate-500 mb-1">
                                        {/* Handle backward compatibility for category */}
                                        {typeof project.category === 'object' ? project.category.sr : project.category}
                                    </p>
                                    {/* Show number of images if available */}
                                    {project.images && (
                                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                                            {project.images.length} images
                                        </span>
                                    )}
                                </div>
                                <button onClick={() => handleDelete(project.id)} className="p-2 text-red-500 hover:bg-red-50 rounded">
                                    <Trash2 className="w-5 h-5" />
                                </button>
                            </div>
                        ))}
                        {projects.length === 0 && !loading && (
                            <p className="text-slate-500 text-center py-8">No projects found.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Admin;
